/* DP product page, v17 premium layer: label reader (tap an ingredient), Buy 2 note,
   free US shipping line, quality row and motion. Loaded by sections/dp-product.liquid.
   Self-contained on purpose: the motion and the ingredient notes mirror dp-premium.js on the
   home page, but nothing here depends on that file, so the home can change on its own. */
(function () {
  'use strict';

  var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches,
    fine = matchMedia('(hover: hover) and (pointer: fine)').matches,
    ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/',
    GEAR = /^DP (Apparel|Gear)$/i,
    currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
    fmt = window.Intl ? new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }) : null;

  function $$(s, el) { return [].slice.call((el || document).querySelectorAll(s)); }
  function money(c) { return fmt ? fmt.format(c / 100) : '$' + (c / 100).toFixed(2); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function replay(el, cls) { el.classList.remove(cls); void el.offsetWidth; el.classList.add(cls); }

  /* ---------- motion, same feel as the home page ---------- */
  function countUp(el) {
    if (!el.hasAttribute('data-final')) el.setAttribute('data-final', el.textContent.trim());
    var txt = el.getAttribute('data-final'), m = txt.match(/^([^\d]*)([\d,]*\.?\d+)(.*)$/);
    el.textContent = txt;
    if (!m || reduce) return;
    var pre = m[1], num = parseFloat(m[2].replace(/,/g, '')), dec = (m[2].split('.')[1] || '').length, post = m[3], t0 = 0, D = 1500;
    function step(t) {
      if (!t0) t0 = t;
      var k = Math.min(1, (t - t0) / D), ease = k === 1 ? 1 : 1 - Math.pow(2, -10 * k);
      el.textContent = pre + (num * ease).toFixed(dec) + post;
      if (k < 1) requestAnimationFrame(step); else el.textContent = txt;
    }
    el.textContent = pre + (0).toFixed(dec) + post;
    requestAnimationFrame(step);
  }

  function splitLines(el) {
    if (el._split) return;
    el._split = true;
    el.innerHTML = el.innerHTML.split(/<br\s*\/?>/i).map(function (p) { return '<span class="ln"><span>' + p.trim() + '</span></span>'; }).join('');
  }

  function show(el) {
    if (el.hasAttribute('data-count')) countUp(el);
    else { el.classList.remove('prep'); el.classList.add('in'); }
  }
  var seen = 'IntersectionObserver' in window ? new IntersectionObserver(function (es) {
    es.forEach(function (e) { if (e.isIntersecting) { seen.unobserve(e.target); show(e.target); } });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.2 }) : null;
  function watch(el) { if (seen) seen.observe(el); else show(el); }

  function tilt(el, max) {
    if (!fine || reduce || el._tilt) return;
    el._tilt = true;
    var r = null, x = 0.5, y = 0.5, raf = 0;
    function paint() {
      raf = 0;
      el.style.setProperty('--ry', ((x - 0.5) * max).toFixed(2) + 'deg');
      el.style.setProperty('--rx', ((0.5 - y) * max).toFixed(2) + 'deg');
      el.style.setProperty('--gx', (x * 100).toFixed(1) + '%');
      el.style.setProperty('--gy', (y * 100).toFixed(1) + '%');
    }
    el.addEventListener('pointerenter', function () { r = el.getBoundingClientRect(); });
    el.addEventListener('pointermove', function (e) {
      if (!r) r = el.getBoundingClientRect();
      x = (e.clientX - r.left) / r.width; y = (e.clientY - r.top) / r.height;
      if (!raf) raf = requestAnimationFrame(paint);
    });
    el.addEventListener('pointerleave', function () { r = null; x = 0.5; y = 0.5; if (!raf) raf = requestAnimationFrame(paint); });
  }

  function magnet(el) {
    if (!el || !fine || reduce || el._mag) return;
    el._mag = true;
    el.classList.add('pdq-mag');
    var r = null;
    el.addEventListener('pointermove', function (e) {
      if (!r) r = el.getBoundingClientRect();
      el.style.setProperty('--tx', ((e.clientX - r.left - r.width / 2) * 0.18).toFixed(1) + 'px');
      el.style.setProperty('--ty', ((e.clientY - r.top - r.height / 2) * 0.3).toFixed(1) + 'px');
    });
    el.addEventListener('pointerleave', function () { r = null; el.style.setProperty('--tx', '0px'); el.style.setProperty('--ty', '0px'); });
  }

  /* ---------- label reader ---------- */
  /* What each ingredient is, in plain words. First match on the full label text wins, so the
     more specific entries come first. The home page entries are the middle block. */
  var INFO = [
    ['creatine (as creatine monohydrate)', 'Creatine monohydrate, the form of creatine used in most of the research on strength and power.*'],
    ['sodium (as sea salt)', 'Sodium from sea salt. Sodium is an electrolyte, one of the minerals you lose in sweat.'],
    ['softgel', 'The softgel shell, made from gelatin, glycerin and water.'],
    ['whey protein isolate', 'Milk protein, filtered to remove most of the fat and lactose. This is where the protein comes from.'],
    ['fava bean protein', 'Protein from fava beans. No dairy, soy or gluten.'],
    ['yestein', 'Protein from fermented nutritional yeast. Together with the fava bean protein, it makes up the protein in each serving.'],
    ['natural flavor', 'Gives it its taste.'],
    ['mct oil', 'A light fat, usually from coconut. Keeps the shake creamy.'],
    ['apple pectin', 'A natural fiber from apples.'],
    ['sunflower lecithin', 'From sunflower seeds. Helps the powder mix smooth with just water.'],
    ['stevia', 'A sweetener from the stevia leaf, used instead of sugar.'],
    ['sea salt', 'A pinch to round out the flavor.'],
    ['silicon dioxide', "Keeps the powder loose so it doesn't cake."],
    ['creatine monohydrate', "The form of creatine used in most of the research on strength and power. It's the only ingredient in the tub.*"],
    ['msm', 'Methylsulfonylmethane, a natural source of sulfur.'],
    ['glucosamine', "GlucosaGreen® glucosamine is made from plants, so it isn't from shellfish."],
    ['turmeric', 'Ground turmeric root.'],
    ['white willow', 'Bark extract from the white willow tree. Talk to your doctor first if you take blood thinners or are sensitive to aspirin.'],
    ['boswellia', 'Resin extract from the Boswellia serrata tree, measured to 65% boswellic acids.'],
    ['black pepper', 'Black pepper extract, measured to 95% piperine.'],
    ['hyaluronic', "Hyaluronic acid. Your body makes it too, and it's found in the fluid around your joints."],
    ['gelatin', "The capsule shell. It's why this formula isn't vegetarian."],
    ['microcrystalline cellulose', 'A plant fiber that helps fill each capsule evenly.'],
    ['magnesium stearate', 'Helps the powder flow when the capsules are filled.'],
    ['magnesium glycinate', "Magnesium bound to glycine, an amino acid. It's the form of magnesium in this formula."],
    ['magnesium citrate', 'Magnesium bound to citric acid.'],
    ['magnesium malate', 'Magnesium bound to malic acid, an acid found in apples.'],
    ['sodium citrate', 'Sodium bound to citric acid. Sodium is an electrolyte, one of the minerals you lose in sweat.'],
    ['potassium', 'Potassium is an electrolyte, one of the minerals you lose in sweat.'],
    ['fish oil', "Fish oil concentrate. It's where the EPA and DHA omega-3s come from."],
    ['collagen', 'Collagen from bovine (cow) hide, broken down into peptides so it mixes easily.'],
    ['taurine', 'An amino acid found naturally in meat and fish.'],
    ['caffeine', 'Natural caffeine from green tea. Count it toward your daily caffeine.'],
    ['l-theanine', 'An amino acid found in tea leaves.'],
    ['polydextrose', 'A soluble fiber, used as the base of the powder.'],
    ['citric acid', "Adds a tart edge. It's the same acid found in lemons."],
    ['sucralose', 'A zero-calorie sweetener.'],
    ['calcium silicate', 'Keeps the powder from clumping.'],
    ['beet', 'Red beet juice powder, used for color.'],
    ['cocoa', 'Cocoa powder, for the chocolate taste and color.'],
    ['hypromellose', 'A plant-based capsule shell made from cellulose.'],
    ['rice maltodextrin', 'A carbohydrate from rice, used to fill each capsule evenly.'],
    ['rice flour', 'Ground rice, used to fill each capsule evenly.'],
    ['l-leucine', 'An amino acid. Here it helps the powder flow when the capsules are filled.'],
    ['probiotic bacteria', 'The probiotic blend. The amount and CFU count on the label cover all the strains listed after it.'],
    ['lactobacillus', 'A probiotic strain. The code after the name identifies the exact strain.'],
    ['bifidobacterium', 'A probiotic strain. The code after the name identifies the exact strain.'],
    ['fructooligosaccharide', 'A prebiotic fiber, often called FOS, found in plants like chicory root and onions.'],
    ['beta-carotene', 'Vitamin A in the form of beta-carotene, the orange pigment in carrots.'],
    ['vitamin e', 'A fat-soluble vitamin.'],
    ['soy isoflavones', "Plant compounds from soybeans. They're why this formula contains soy."],
    ['resveratrol', 'A plant compound found in grape skins.']
  ];

  function listify(s) {
    var a = s.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
    return a.length > 1 ? a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1] : a.join('');
  }
  function about(full, name) {
    var n = full.toLowerCase();
    for (var i = 0; i < INFO.length; i++) if (n.indexOf(INFO[i][0]) > -1) return INFO[i][1];
    var inner = (full.match(/\(([^)]*)\)/) || [])[1] || '';
    if (/blend/i.test(name) && inner.indexOf(',') > -1) return 'A blend of ' + listify(inner) + '.';
    var as = full.match(/\(as\s+([^)]+)\)/i);
    if (as) return 'Here in the form of ' + as[1].trim() + '.';
    return 'Part of the formula, exactly as listed on the label.';
  }

  /* Split a label line into ingredients: commas, semicolons, ": " and ". " at the top level
     (not inside brackets), and never the comma in a number like 1,000. */
  function parts(s) {
    var out = [], d = 0, cur = '';
    for (var i = 0; i < s.length; i++) {
      var ch = s.charAt(i), prev = s.charAt(i - 1), next = s.charAt(i + 1), cut = false;
      if (ch === '(') d++;
      if (ch === ')') d = Math.max(0, d - 1);
      if (!d) {
        if (ch === ',') cut = !(/\d/.test(prev) && /\d/.test(next));
        else if (ch === ';') cut = true;
        else if (ch === ':' || ch === '.') cut = /\s/.test(next);
      }
      if (cut) { out.push(cur); cur = ''; } else cur += ch;
    }
    out.push(cur);
    return out.map(function (x) { return x.replace(/\s+/g, ' ').trim().replace(/[.:;,]+$/, ''); }).filter(Boolean);
  }

  var AMT = /(\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)\s?(mcg|mg|g|iu)\b/gi;
  function chips(s) {
    return parts(s).map(function (full) {
      var outside = full.replace(/\s*\([^)]*\)/g, ''),
        m = outside.match(AMT),
        amt = m ? m[m.length - 1] : '',
        name = outside.replace(AMT, '').replace(/\s+/g, ' ').replace(/[\s.:;,]+$/, '').trim() || full;
      return '<button type="button" class="pdl-ing" aria-pressed="false" data-full="' + esc(full) + '" data-name="' + esc(name) + '" data-amt="' + esc(amt) + '">' +
        esc(name) + (amt ? '<small>' + esc(amt) + '</small>' : '') + '</button>';
    }).join('');
  }

  function buildLabel(box) {
    if (box._built) return;
    box._built = true;
    var raw = box.getAttribute('data-ings') || '', split = raw.split(/other ingredients:/i),
      rawEl = box.querySelector('.pdl-raw'), ex = box.querySelector('.pdl-explain');
    if (!rawEl || !ex) return;
    var html = '<div class="pdl-ings">' + chips(split[0]) + '</div>' +
      (split[1] ? '<p class="pdl-other-k">Other ingredients</p><div class="pdl-ings pdl-other">' + chips(split[1]) + '</div>' : '');
    rawEl.insertAdjacentHTML('afterend', html);
    rawEl.hidden = true;
    function open(b) {
      $$('.pdl-ing', box).forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
      ex.hidden = false;
      ex.innerHTML = '<b>' + esc(b.getAttribute('data-name')) + (b.getAttribute('data-amt') ? ' <em>' + esc(b.getAttribute('data-amt')) + ' per serving</em>' : '') + '</b>' +
        '<p>' + esc(about(b.getAttribute('data-full'), b.getAttribute('data-name'))) + '</p>';
      if (!reduce) replay(ex, 'pdq-enter');
    }
    box.addEventListener('click', function (e) { var b = e.target.closest('.pdl-ing'); if (b) open(b); });
    var first = box.querySelector('.pdl-ing');
    if (first) open(first);
  }

  /* ---------- buy box: Buy 2 note and free-shipping line ---------- */
  function initBuy(root) {
    var form = root.querySelector('form[data-dp-form]');
    if (!form) return;
    var qty = form.querySelector('input[name="quantity"]'),
      idIn = form.querySelector('input[name="id"]'),
      box = qty && qty.closest('.qty'),
      ship = root.querySelector('[data-shipline]'),
      b2 = root.querySelector('[data-b2]'),
      pb = root.querySelector('.pricebox[data-price]'),
      src = root.querySelector('[data-variants]'),
      base = pb ? +pb.getAttribute('data-price') || 0 : 0,
      prices = {};
    magnet(form.querySelector('.atc-btn'));
    if (!ship && !b2) return;
    if (src) { try { JSON.parse(src.textContent).forEach(function (v) { prices[v.id] = v.price; }); } catch (e) {} }

    var FREE = (ship && +ship.getAttribute('data-free')) || 5000,
      cartSupp = ship ? +ship.getAttribute('data-cart') || 0 : 0,
      cartB2 = b2 ? +b2.getAttribute('data-b2-cart') || 0 : 0,
      handles = b2 ? (b2.getAttribute('data-b2-handles') || '').split(',') : [];

    function price() { var id = idIn && idIn.value; return id && prices[id] != null ? prices[id] : base; }
    function count() { return Math.max(1, Math.min(20, parseInt(qty && qty.value, 10) || 1)); }
    function set(el, html) { if (el && el.innerHTML !== html) el.innerHTML = html; }

    function paintShip() {
      if (!ship) return;
      var n = count(), add = price() * n, left = FREE - cartSupp, what = n > 1 ? 'these ' + n : 'this',
        have = Math.min(100, cartSupp / FREE * 100), plus = Math.max(0, Math.min(100 - have, add / FREE * 100));
      set(ship.querySelector('[data-ship-text]'), left <= 0 ? '<b>Your order ships free</b> in the US.'
        : add >= left ? '<b>Add ' + what + ' and your order ships free</b> in the US.'
        : 'Add ' + what + " and you're <b>" + money(left - add) + '</b> away from free US shipping.');
      ship.classList.toggle('done', left <= 0);
      ship.classList.toggle('no-cart', cartSupp <= 0);
      var h = ship.querySelector('.have'), a = ship.querySelector('.add');
      if (h) h.style.width = have.toFixed(1) + '%';
      if (a) { a.style.left = have.toFixed(1) + '%'; a.style.width = plus.toFixed(1) + '%'; }
    }

    function paintB2() {
      if (!b2) return;
      var n = count(), two = b2.querySelector('[data-b2-two]'), html;
      if (n >= 2) {
        html = '<b>Buy 2, save 10%: you qualify.</b> ' + (cartB2 === 0 && n === 2
          ? "That's <b>" + money(Math.round(price() * n * 0.1)) + '</b> off, applied automatically at checkout.'
          : 'The discount comes off automatically at checkout.');
      } else if (cartB2 > 0) {
        html = '<b>Buy 2, save 10%.</b> You already have an eligible supplement in your cart, so adding this one qualifies. The discount comes off automatically at checkout.';
      } else {
        html = '<b>Buy 2, save 10%.</b> Get 2, or pair it with another eligible supplement. The discount comes off automatically at checkout.';
      }
      set(b2.querySelector('[data-b2-text]'), html);
      b2.classList.toggle('on', n >= 2 || cartB2 > 0);
      if (two) two.hidden = n >= 2;
    }

    function paint() { paintShip(); paintB2(); }
    function later() { setTimeout(paint, 0); }

    if (qty) { qty.addEventListener('input', paint); qty.addEventListener('change', paint); }
    $$('[data-q]', form).forEach(function (b) { b.addEventListener('click', later); });
    var opts = root.querySelector('[data-opts]');
    if (opts) opts.addEventListener('change', later);

    var two = b2 && b2.querySelector('[data-b2-two]');
    if (two && qty) two.addEventListener('click', function () {
      qty.value = Math.max(2, count());
      paint();
      if (box && !reduce) replay(box, 'pulse');
      var t = b2.querySelector('[data-b2-text]');
      if (t) { t.setAttribute('tabindex', '-1'); t.focus({ preventScroll: true }); }
    });

    function fromCart(cart) {
      var items = (cart && cart.items) || [];
      cartSupp = items.reduce(function (s, it) {
        return GEAR.test(it.product_type || '') ? s : s + (it.final_line_price != null ? it.final_line_price : it.line_price);
      }, 0);
      cartB2 = items.reduce(function (s, it) { return handles.indexOf(it.handle) > -1 ? s + it.quantity : s; }, 0);
      paint();
    }
    /* dp-product.js sends this after every add to cart */
    document.addEventListener('dp:cart-updated', function (e) { fromCart(e.detail); });
    /* coming back with the browser's back button can show an old page: re-read the cart */
    addEventListener('pageshow', function (e) {
      if (!e.persisted || !window.fetch) return;
      fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(fromCart).catch(function () {});
    });

    paint();
  }

  function init(root) {
    if (!root || root._v17) return;
    root._v17 = true;
    if (!reduce) $$('.pdq-split', root).forEach(splitLines);
    $$('.pdq-split, .pdl-stats [data-count]', root).forEach(watch);
    if (!reduce) $$('[data-shipline], [data-quality]', root).forEach(function (el) { el.classList.add('prep'); watch(el); });
    $$('[data-tilt]', root).forEach(function (el) { tilt(el, 6); });
    $$('[data-ings]', root).forEach(buildLabel);
    initBuy(root);
  }

  function boot() { $$('[data-dpp]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { init(e.target.querySelector('[data-dpp]')); });
})();
